/**
 * Dice — Style Bible §3.
 *
 * A 1980s board game die is ivory plastic with black pips, drawn face-on. The
 * previous isometric three-face die was a videogame convention; a printed board
 * game shows you the face and nothing else, because that is all that matters.
 */

import {
  DICE_SKINS,
  DICE_SPEC,
  HALFTONE,
  INK,
  LINE,
  MISREGISTRATION,
  SPOT,
  type DiceSkin,
} from './tokens.js';
import {
  circle,
  defs,
  halftonePattern,
  misregistered,
  n,
  path,
  roundedRectPath,
  svg,
} from './svg.js';

export type DiceFace = 1 | 2 | 3 | 4 | 5 | 6;

/** Standard d6 pip layout on a 3×3 grid, as (col, row) with 0..2 indices. */
const PIP_LAYOUT: Readonly<Record<DiceFace, readonly [number, number][]>> = {
  1: [[1, 1]],
  2: [[0, 0], [2, 2]],
  3: [[0, 0], [1, 1], [2, 2]],
  4: [[0, 0], [2, 0], [0, 2], [2, 2]],
  5: [[0, 0], [2, 0], [1, 1], [0, 2], [2, 2]],
  6: [[0, 0], [2, 0], [0, 1], [2, 1], [0, 2], [2, 2]],
};

export interface DiceRenderOptions {
  readonly skin: DiceSkin;
  readonly face: DiceFace;
  readonly size?: number;
  /** Ring the die when a class ability fires on this face. */
  readonly active?: boolean;
  /** `view` is accepted for call-site compatibility; a printed die has one view. */
  readonly view?: 'iso' | 'flat';
}

export function renderDie(opts: DiceRenderOptions): string {
  const { skin, face, active = false } = opts;
  const size = opts.size ?? DICE_SPEC.canvas;
  const spec = DICE_SKINS[skin];
  const s = size / 256;

  const inset = size * 0.08;
  const body = size - inset * 2;
  const radius = body * DICE_SPEC.cornerRadiusRatio;
  const stroke = LINE.outline * s;

  const screenId = `dscreen-${skin}`;
  const head = defs(
    spec.screened ? halftonePattern(screenId, INK.faded, HALFTONE.light.dot, HALFTONE.light.grid) : '',
  );

  const shape = roundedRectPath(inset, inset, body, body, radius);

  // Colour plate first, offset from the key line (Style Bible §2.3).
  const plate = misregistered(
    `die:${skin}:${face}`,
    size,
    MISREGISTRATION,
    path(shape, { fill: spec.body }) +
      (spec.screened ? path(shape, { fill: `url(#${screenId})` }) : ''),
  );

  // Key line stays put — that is what makes the offset read as misregistration.
  const keyline = path(shape, {
    fill: 'none',
    stroke: INK.key,
    'stroke-width': stroke,
    'stroke-linejoin': 'round',
  });

  const r = (body * DICE_SPEC.pipDiameterRatio) / 2;
  const cellInset = body * 0.24;
  const step = (body - cellInset * 2) / 2;
  const pips = PIP_LAYOUT[face]
    .map(([col, row]) =>
      circle({
        cx: inset + cellInset + col * step,
        cy: inset + cellInset + row * step,
        r,
        fill: spec.pip,
      }),
    )
    .join('');

  // A printed "critical" marker: a ring, not a glow. Print has no glow.
  const activeRing = active
    ? path(roundedRectPath(inset * 0.45, inset * 0.45, size - inset * 0.9, size - inset * 0.9, radius * 1.3), {
        fill: 'none',
        stroke: SPOT.red,
        'stroke-width': LINE.structure * s * 1.4,
        'stroke-dasharray': `${n(10 * s)} ${n(7 * s)}`,
      })
    : '';

  return svg(size, size, head + plate + keyline + pips + activeRing);
}

/** All 8 skins × 6 faces for the manifest. */
export function allDiceAssets(): { id: string; svg: string }[] {
  const out: { id: string; svg: string }[] = [];
  for (const skin of Object.keys(DICE_SKINS) as DiceSkin[]) {
    for (const face of [1, 2, 3, 4, 5, 6] as DiceFace[]) {
      out.push({ id: `dice.${skin}.${face}`, svg: renderDie({ skin, face }) });
      // The flat id is kept so existing manifests and callers keep resolving.
      out.push({ id: `dice.${skin}.${face}.flat`, svg: renderDie({ skin, face }) });
    }
  }
  return out;
}
